import { useEffect, useState } from 'react';
import api from '../../../utils/api';
import { PartnerEndpoints } from '../../../utils/endpoints';
import { Constants, NAME_PENDING } from '../../../utils/constants';
import { formatCount, formatNaira } from '../../../utils/money';
import { formatLagos } from '../../../utils/datetime';
import PageHeader from '../../shell/PageHeader';
import {
  Card,
  DataTable,
  DateRangeFilter,
  EmptyState,
  FilterSelect,
  Pagination,
  SearchInput,
  StatusPill,
  TableCard,
} from '../../ui';
import { useServerTable } from '../../../hooks/useServerTable';

const AccountField = ({ label, value }) => (
  <div>
    <dt className="text-base text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">{label}</dt>
    <dd className="text-f-h6 font-medium text-theme-headings dark:text-themedark-headings">{value || '—'}</dd>
  </div>
);

const Payouts = () => {
  const [details, setDetails] = useState(null);

  const table = useServerTable(PartnerEndpoints.payouts, {
    sort: '-created_at',
    filters: { status: '' },
    fallbackError: 'Could not load payouts. Please try again.',
  });

  useEffect(() => {
    let active = true;
    const fetchSettlementAccount = async () => {
      try {
        const response = await api.get(PartnerEndpoints.partnerDetails);
        if (active) setDetails(response.data?.data ?? null);
      } catch {
        if (active) setDetails(null);
      }
    };
    fetchSettlementAccount();
    return () => {
      active = false;
    };
  }, []);

  // Without partner.payouts.view the settlement keys are absent rather than null, so only
  // `in` distinguishes "not permitted" from the legitimate null of "no account on file yet".
  const canSeeSettlementAccount = details !== null && 'account_number' in details;
  const hasSettlementAccount = canSeeSettlementAccount && details.account_number !== null;

  const columns = [
    {
      key: 'created_at',
      header: 'Date (WAT)',
      sortKey: 'created_at',
      sortDefault: 'desc',
      render: (row) => <span className="whitespace-nowrap">{formatLagos(row.created_at)}</span>,
      filter: (
        <DateRangeFilter
          label="Date"
          from={table.from}
          to={table.to}
          onFromChange={table.setFrom}
          onToChange={table.setTo}
        />
      ),
    },
    { key: 'reference', header: 'Reference', render: (row) => row.reference || '—' },
    {
      key: 'cash_tap_transaction_id',
      header: 'Tap Transaction ID',
      render: (row) => row.cash_tap_transaction_id || '—',
    },
    {
      key: 'cash_tap_reference',
      header: 'Tap Reference',
      render: (row) => row.cash_tap_reference || '—',
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (row) => <StatusPill status={row.status} />,
      filter: (
        <FilterSelect
          value={table.filters.status ?? ''}
          onChange={(value) => table.setFilter('status', value)}
          options={Constants.payoutStatuses}
          label="All"
          ariaLabel="Filter by status"
        />
      ),
    },
    {
      key: 'amount',
      header: 'Net Amount',
      align: 'right',
      sortKey: 'amount',
      sortDefault: 'desc',
      render: (row) => formatNaira(row.amount),
    },
  ];

  return (
    <>
      <PageHeader
        title="Payouts"
        breadcrumbs={[{ label: 'Home', to: '/layout/dashboard' }, { label: 'Payouts' }]}
      />

      {canSeeSettlementAccount && (
        <Card title="Settlement account" className="mb-4">
          {hasSettlementAccount ? (
            <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <AccountField label="Account name" value={details.bank_account_name ?? NAME_PENDING} />
              <AccountField label="Account number" value={details.account_number} />
              <AccountField label="Bank name" value={details.bank_name} />
              <AccountField label="Bank code" value={details.bank_code} />
            </dl>
          ) : (
            <EmptyState
              title="No settlement account on file"
              message="Contact support to add one."
            />
          )}
        </Card>
      )}

      <p className="mb-4 text-base text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
        Every settlement CashAfrica has paid out for your merchants. Amounts are net of commission, not the tap amount,
        and a failed payout carries no provider detail — quote its reference to support. Partners that settle inside
        CashCard see a row only once the settlement has completed, and have no history before September 2026.
      </p>

      <TableCard
        title="Payouts"
        summary={{ label: 'Payouts', value: formatCount(table.total), loading: table.loading }}
        toolbar={
          <SearchInput
            value={table.search}
            onChange={table.setSearch}
            placeholder="Search reference or tap transaction ID"
          />
        }
        footer={
          table.total > 0 && (
            <Pagination
              page={table.page}
              pageCount={table.pageCount}
              pageSize={table.pageSize}
              onPageChange={table.setPage}
              onPageSizeChange={table.setPageSize}
            />
          )
        }
      >
        <DataTable
          columns={columns}
          rows={table.rows}
          rowKey={(row) => row.id}
          serial
          serialStart={table.page * table.pageSize}
          loading={table.loading}
          error={table.error}
          invalidDateRange={table.invalidDateRange}
          sort={table.sort}
          onSortChange={table.setSort}
          empty={{
            title: 'No payouts',
            message: 'No payouts match the selected filters.',
          }}
        />
      </TableCard>
    </>
  );
};

export default Payouts;
